import React, { useEffect } from "react";
import {
  Button,
  Box,
  useDisclosure,
  Text,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tooltip,
} from "@chakra-ui/react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import * as analytics from "utils/analytics";
// Components
import ProtectedRoute from "components/protected-route";
import { Page } from "components/page";
import AddProfileModal from "components/mentions/AddProfileModal";
import Profiles from "components/mentions/Profiles";

// Utils
import { getAccount } from "utils/sessionHelper";

export default function Mentions(_props: any) {
  const router = useRouter();
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";
  const [isSessionLoaded, setIsSessionLoaded] = React.useState<boolean>(false);
  const [selectedTabIdx, setSelectedTabIdx] = React.useState<number>(0);
  const [account, setAccount] = React.useState<any>(null);
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  useEffect(() => {
    (async () => {
      if (!loading && session && !isSessionLoaded) {
        const account = await getAccount(session);
        setAccount(account);
        setIsSessionLoaded(true);
        analytics.log("mention_notification_viewed");
      }
    })();
  }, [loading, session, isSessionLoaded]);

  if (!(loading || session)) {
    return <ProtectedRoute />;
  }
  if (!account) return null;
  const isMaxProfiles = account.mentionsProfiles?.length >= 5;
  return (
    <Page
      forceCloseScheduler={true}
      title="Mentions"
      titleIconWrapperProps={{ gap: 3 }}
      description="See and reply to recent posts that @mention you or your company."
      descriptionWrapperProps={{ pb: "5" }}
    >
      <Box mb={4}>
        <Tooltip
          label="You can only add up to 5 profile/companies"
          aria-label="You can only add up to 5 profile/companies"
          isDisabled={!isMaxProfiles}
        >
          <Button
            variant="primary"
            onClick={onModalOpen}
            isDisabled={isMaxProfiles}
          >
            + Add Profile / Company{" "}
          </Button>
        </Tooltip>
        {account.subscription?.plan == "enterprise" ? (
          <AddProfileModal
            isOpen={isModalOpen}
            onClose={onModalClose}
            onModalClose={onModalClose}
            session={session}
            account={account}
            onAdding={(username) => {
              setIsSessionLoaded(false);
              router.push({
                pathname: router.pathname,
                query: { profile: username },
              });
            }}
          />
        ) : (
          <Modal
            isOpen={isModalOpen}
            onClose={onModalClose}
            variant="upgrade"
            size="lg"
            isCentered
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Upgrade to Pro to unleash @mentions</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text as="p" fontSize="sm">
                  ✅ Add up to 5 different accounts (including company accounts)
                </Text>
                <Text as="p" fontSize="sm">
                  ✅ Receive email notifications
                </Text>
                <Text as="p" fontSize="sm">
                  ✅ Receive Slack notifications
                </Text>
                <br />
                <Text as="p" fontSize="sm">
                  And get additional Taplio features like Lead Database and
                  higher limits on AI and import credits.
                </Text>
              </ModalBody>

              <ModalFooter>
                <Button
                  variant="primary"
                  size="lg"
                  borderColor="white"
                  onClick={async () => {
                    router.push("/pricing");
                  }}
                >
                  Upgrade to Pro
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </Box>
      <Profiles
        account={account}
        session={session}
        selectedTabIdx={selectedTabIdx}
        setSelectedTabIdx={setSelectedTabIdx}
        onRemoving={() => {
          setIsSessionLoaded(false);
          setSelectedTabIdx(0);
          router.push({
            pathname: router.pathname,
          });
        }}
        onSessionUpdate={() => {
          setIsSessionLoaded(false);
        }}
      />
    </Page>
  );
}
